// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
  // Try to get from localStorage first (fallback)
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export async function fetchPendingTickets(page = 1, limit = 16, filterParams = {}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query string with pagination and filter parameters
  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('limit', limit.toString());

  if (filterParams.categoryId) {
    queryParams.append('category', filterParams.categoryId);
  }
  if (filterParams.customerId) {
    queryParams.append('customer', filterParams.customerId);
  }
  if (filterParams.date) {
    queryParams.append('date', filterParams.date);
  }

  const url = `${API_BASE_URL}api/v1/dashboard/ticket/pending?${queryParams.toString()}`;

  console.log('Making pending tickets request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch pending tickets';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function fetchClosedTickets(page = 1, limit = 16, filterParams = {}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query string with pagination and filter parameters
  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('limit', limit.toString());

  if (filterParams.categoryId) {
    queryParams.append('category', filterParams.categoryId);
  }
  if (filterParams.customerId) {
    queryParams.append('customer', filterParams.customerId);
  }
  if (filterParams.date) {
    queryParams.append('date', filterParams.date);
  }

  const url = `${API_BASE_URL}api/v1/dashboard/ticket/closed?${queryParams.toString()}`;

  console.log('Making closed tickets request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch closed tickets';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function fetchTicketDetails(ticketId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making ticket details request to:', `${API_BASE_URL}api/v1/dashboard/ticket/${ticketId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/${ticketId}`, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch ticket details';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function closeTicket(ticketId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making close ticket request to:', `${API_BASE_URL}api/v1/dashboard/ticket/close/${ticketId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/close/${ticketId}`, {
    method: 'PATCH',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to close ticket';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteTicket(ticketId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making delete ticket request to:', `${API_BASE_URL}api/v1/dashboard/ticket/${ticketId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/${ticketId}`, {
    method: 'DELETE',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to delete ticket';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function exportTickets(status = 'pending') {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  status = status.trim();
  const url = `${API_BASE_URL}api/v1/dashboard/ticket/export?status=${status}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to export tickets';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  // Get the blob from the response
  const blob = await response.blob();
  // Create a download link
  const urlObj = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = urlObj;
  a.download = `tickets_export_${status}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(urlObj);
  document.body.removeChild(a);
  return { success: true };
}

export async function searchCategories(name) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/category/search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name })
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Search failed' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  console.log('Categories search result:', data);
  return data;
}